import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
SyntaxHighlighter.registerLanguage('python', python);
import Win1 from './datasets/market-driver-waterfall-card';
import { breakpoints } from '@/layouts/breakpoints';

const StyledComp = styled.div`
  background: var(--white);
  color: var(--black);
  max-width: 1400px;
  margin: 0 auto;
  padding: 96px 64px;

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 64px;
  }

  .eyebrow {
    font-size: 14px;
    color: rgba(var(--blackr), 0.55);
    margin: 0 0 20px;
    letter-spacing: 0.01em;
  }

  h2 {
    font-size: clamp(32px, 4vw, 52px);
    line-height: 1.1;
    font-weight: 400;
    letter-spacing: -0.01em;
    margin: 0;
  }

  .explore-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    background-color: transparent;
    color: var(--black);
    cursor: pointer;
    white-space: nowrap;
    transition-duration: 0.3s;
    margin-top: 8px;

    &:hover {
      background-color: #eee;
    }
  }

  .explore-btn svg {
    padding-left: 10px;
    transition-duration: 0.3s;
  }

  .explore-btn:hover svg {
    transform: translateX(2px);
  }

  .grid {
    border-top: 1px solid rgba(var(--blackr), 0.1);
  }

  .row {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
    gap: 64px;
    padding: 56px 0;
    border-bottom: 1px solid rgba(var(--blackr), 0.1);

    /* vertical divider between the two columns */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: calc(0.85 / (0.85 + 1.15) * 100% - 32px);
      width: 1px;
      background: rgba(var(--blackr), 0.1);
    }

    /* small dot marker where the vertical divider meets the row's top edge */
    &::after {
      content: '';
      position: absolute;
      top: -1px;
      left: calc(0.85 / (0.85 + 1.15) * 100% - 32px);
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: var(--black);
      transform: translate(-1.5px, -1.5px);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .col-left {
    display: flex;
    flex-direction: column;
  }

  .col-right {
    min-width: 0;
    display: flex;
    &.window{
      > div{
        width: 100%;
      }
    }
  }

  h3 {
    font-size: 22px;
    font-weight: 500;
    margin: 0 0 16px;
    letter-spacing: -0.01em;
  }

  .description {
    font-size: 16px;
    line-height: 1.6;
    color: var(--grey);
    margin: 0 0 32px;
    max-width: 42ch;

    a {
      color: var(--grey);
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        color: var(--black);
      }
    }
  }

  .models {
    display: flex;
    flex-wrap: wrap;
    gap: 32px 48px;
    margin-top: auto;
  }

  .model {
    min-width: 180px;
  }

  .model-name {
    font-size: 16px;
    font-weight: 500;
    margin: 0 0 6px;
  }

  .model-desc {
    font-size: 15px;
    line-height: 1.5;
    color: var(--grey);
    margin: 0;
  }

  @media (max-width: 900px) {
    padding: 56px 24px;
    padding-bottom: 0px;

    .header {
      flex-direction: column;
    }

    .row {
      grid-template-columns: 1fr;
      gap: 32px;

      &::before,
      &::after {
        display: none;
      }
    }
  }
`;

const CODE_STYLES = {
  border: '1px solid rgba(var(--greyr),0.3)',
  backgroundColor: 'white',
  borderRadius: '16px',
  width: '100%',
  fontSize: '13px'

}

const Comp = () => {
  const navigate = useNavigate();
  return (
    <StyledComp>
      <div className="header">
        <div>
          <p className="eyebrow">AxionQuant SDK</p>
          <h2 className="serif">
            Or build anything with
            <br />powerful Python &amp; TypeScript SDKs
          </h2>
        </div>
        <button className="explore-btn" onClick={() => navigate('/docs')}>Explore docs <ChevronRight size={18} /></button>
      </div>
      <div className="grid">
        <div className="row">
          <div className="col-left">
            <h3>Core</h3>
            <p className="description">
              One financial data SDK for 16 data sources - stocks, crypto, forex,
              fundamentals, SEC filings, sentiment, and more. Every response
              returns as a clean DataFrame.
            </p>
            <div className="models">
              <div className="model">
                <p className="model-name">Market Data</p>
                <p className="model-desc">
                  Daily and hourly prices with decades of history across every asset class
                </p>
              </div>
              <div className="model">
                <p className="model-name">Fundamentals & Filings</p>
                <p className="model-desc">
                  Financials, earnings, and SEC filings, structured and searchable
                </p>
              </div>
              <div className="model">
                <p className="model-name">Sentiment & Alt Data</p>
                <p className="model-desc">
                  Sentiment, ESG, econ, and web traffic in the same client
                </p>
              </div>
            </div>
          </div>
          <div className="col-right">
            <SyntaxHighlighter
              language='python'
              style={oneLight}
              customStyle={CODE_STYLES}
            >
              {`from axion import Axion, graph, utils
client = Axion(api_key="YOUR_API_KEY")

cpi = utils.df(client.econ.dataset("CPIAUCSL"))
gdp = utils.df(client.econ.dataset("GDP"))

econ = cpi.merge(
  gdp,
  on="date",
  suffixes=("_cpi", "_gdp")
)

graph(
    econ,
    x="date",
    lines=["value_cpi", "value_gdp"],
    title="US CPI vs. GDP",
)`}
            </SyntaxHighlighter>
          </div>
        </div>
        <div className="row">
          <div className="col-left">
            <h3>Visualization & Modeling</h3>
            <p className="description">
              Chart any response in one line, then train a forecast on the
              same DataFrame. Regression, beta, and LSTM models included.
            </p>
            <div className="models">
              <div className="model">
                <p className="model-name">visualize + models</p>
                <p className="model-desc">
                  Interactive charts and forecasting, chained in one call
                </p>
              </div>
            </div>
          </div>
          <div className="col-right window">
            <Win1/>
          </div>
        </div>
        <div className="row">
          <div className="col-left">
            <h3>Utils</h3>
            <p className="description">
              Concurrency, caching, and DataFrame helpers built for
              pipelines that touch hundreds of tickers at once.
            </p>
            <div className="models">
              <div className="model">
                <p className="model-name">utils module</p>
                <p className="model-desc">
                  Concurrent fetch and caching for large-scale research
                </p>
              </div>
            </div>
          </div>
          <div className="col-right">
              <SyntaxHighlighter
              language='python'
              style={oneLight}
              customStyle={CODE_STYLES}
            >
{` from axion import Axion, utils, models

client = Axion(api_key="YOUR_API_KEY")

tickers = ["AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META", "TSLA"]

def fetch_history(row):
    prices = client.stocks.prices(t, from_date="2021-01-01")
    return (row["ticker"], prices)

history = {}
utils.work(utils.df([{"ticker": t} for t in tickers]), fetch_history, history)

for ticker, prices in history.items():
    models.lstm(utils.df(prices),
      x="time",
      target="close",
      n_preds=30,
      scale="D"
    )

`}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
